import React, { useState } from 'react';
import { Language, Plan, ServiceDefinition, IntegrationApp, AppConfig, DataSourceMode } from '../../types';
import { TRANSLATIONS } from '../../constants';
import AdminDashboard from '../../components/Admin/AdminDashboard';
import AdminPermissions from '../../components/Admin/AdminPermissions';

interface AdminTemplateProps {
  language: Language;
  onBack: () => void;
  // Data props
  plans: Plan[];
  setPlans: React.Dispatch<React.SetStateAction<Plan[]>>;
  services: ServiceDefinition[];
  setServices: React.Dispatch<React.SetStateAction<ServiceDefinition[]>>;
  integrations: IntegrationApp[];
  setIntegrations: React.Dispatch<React.SetStateAction<IntegrationApp[]>>;
  // Config props
  config: AppConfig;
  onUpdateConfig: (config: Partial<AppConfig>) => void;
}

const AdminTemplate: React.FC<AdminTemplateProps> = ({
  language, onBack, plans, setPlans, services, setServices, integrations, setIntegrations, config, onUpdateConfig
}) => {
  const t = TRANSLATIONS[language];
  const isRtl = language === 'ar';
  const [activeTab, setActiveTab] = useState<'overview' | 'plans' | 'services' | 'integrations' | 'permissions'>('overview');
  const [editingItem, setEditingItem] = useState<any | null>(null);
  const [editType, setEditType] = useState<'plan' | 'service' | 'integration' | null>(null);

  // --- CRUD Handlers ---

  const handleToggleVisibility = (id: string, type: 'plan' | 'service' | 'integration') => {
    if (type === 'plan') {
      setPlans(prev => prev.map(p => p.id === id ? { ...p, hidden: !p.hidden } : p));
    } else if (type === 'service') {
      setServices(prev => prev.map(s => s.id === id ? { ...s, hidden: !s.hidden } : s));
    } else if (type === 'integration') {
      setIntegrations(prev => prev.map(i => i.id === id ? { ...i, hidden: !i.hidden } : i));
    }
  };

  const handleSavePlan = (plan: Plan) => {
    if (plans.find(p => p.id === plan.id)) {
      setPlans(plans.map(p => p.id === plan.id ? plan : p));
    } else {
      setPlans([...plans, plan]);
    }
    setEditingItem(null);
  };

  const handleDeletePlan = (id: string) => {
    if (window.confirm(isRtl ? 'هل أنت متأكد من حذف هذه الخطة نهائياً؟' : 'Are you sure you want to delete this plan permanently?')) {
      setPlans(plans.filter(p => p.id !== id));
    }
  };

  const handleSaveService = (service: ServiceDefinition) => {
    if (services.find(s => s.id === service.id)) {
      setServices(services.map(s => s.id === service.id ? service : s));
    } else {
      setServices([...services, service]);
    }
    setEditingItem(null);
  };

  const handleDeleteService = (id: string) => {
    if (window.confirm(isRtl ? 'هل أنت متأكد من حذف هذه الخدمة نهائياً؟' : 'Are you sure you want to delete this service permanently?')) {
      setServices(services.filter(s => s.id !== id));
    }
  };

  const handleSaveIntegration = (app: IntegrationApp) => {
    if (integrations.find(a => a.id === app.id)) {
      setIntegrations(integrations.map(a => a.id === app.id ? app : a));
    } else {
      setIntegrations([...integrations, app]);
    }
    setEditingItem(null);
  };

  const handleDeleteIntegration = (id: string) => {
    if (window.confirm(isRtl ? 'هل أنت متأكد من حذف هذا التطبيق نهائياً؟' : 'Are you sure you want to delete this integration permanently?')) {
      setIntegrations(integrations.filter(a => a.id !== id));
    }
  };

  const handleUpdateMode = (mode: DataSourceMode) => {
     onUpdateConfig({ dataSourceMode: mode });
  };

  // --- Modal Form Renderer ---
  const renderForm = () => {
    if (!editingItem) return null;

    const commonInput = (key: string, label: string, type: string = 'text') => (
      <div className="mb-4">
        <label className="block text-xs font-bold text-slate-500 mb-1">{label}</label>
        <input 
          type={type} 
          value={editingItem[key] || ''} 
          onChange={e => setEditingItem({...editingItem, [key]: e.target.value})}
          className="w-full bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-xl p-3 text-sm dark:text-white outline-none focus:border-emerald-500"
        />
      </div>
    );

    const limitInput = (key: keyof Plan['limits'], label: string) => {
        const val = editingItem.limits?.[key];
        const isUnlimited = val === -1;
        return (
            <div className="mb-4 p-3 bg-slate-50 dark:bg-white/5 rounded-xl border border-slate-200 dark:border-white/5">
                <div className="flex justify-between items-center mb-2">
                    <label className="text-xs font-bold text-slate-500">{label}</label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input 
                            type="checkbox" 
                            checked={isUnlimited} 
                            onChange={(e) => {
                                const newLimits = { ...editingItem.limits, [key]: e.target.checked ? -1 : 10 };
                                setEditingItem({ ...editingItem, limits: newLimits });
                            }} 
                            className="accent-emerald-500"
                        />
                        <span className="text-[10px] font-black text-emerald-500 uppercase">Unlimited (∞)</span>
                    </label>
                </div>
                {!isUnlimited && (
                    <input 
                        type="number"
                        value={val}
                        onChange={(e) => {
                            const newLimits = { ...editingItem.limits, [key]: parseInt(e.target.value) || 0 };
                            setEditingItem({ ...editingItem, limits: newLimits });
                        }}
                        className="w-full bg-white dark:bg-black/40 border border-slate-200 dark:border-white/10 rounded-lg p-2 text-sm dark:text-white outline-none focus:border-emerald-500"
                    />
                )}
            </div>
        );
    };

    const commonTextArea = (key: string, label: string) => (
      <div className="mb-4">
        <label className="block text-xs font-bold text-slate-500 mb-1">{label}</label>
        <textarea 
          value={editingItem[key] || ''} 
          onChange={e => setEditingItem({...editingItem, [key]: e.target.value})}
          className="w-full bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-xl p-3 text-sm dark:text-white outline-none focus:border-emerald-500 h-24 resize-none"
        />
      </div>
    );

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setEditingItem(null)}></div>
        <div className="relative bg-white dark:bg-[#15151a] w-full max-w-lg rounded-[24px] shadow-2xl p-6 border border-white/10 overflow-y-auto max-h-[90vh] custom-scrollbar">
          <h3 className="text-xl font-black text-slate-800 dark:text-white mb-6">
            {isRtl ? 'تعديل العنصر' : 'Edit Item'}
          </h3>
          
          {editType === 'plan' && (
            <>
              {commonInput('name', isRtl ? 'اسم الباقة' : 'Plan Name')}
              {commonInput('price', isRtl ? 'السعر' : 'Price')}
              {commonInput('period', isRtl ? 'الفترة' : 'Period')}
              {commonInput('icon', 'FontAwesome Icon')}
              
              <div className="my-6 pt-4 border-t border-slate-100 dark:border-white/5">
                  <h4 className="text-sm font-black text-emerald-500 mb-4 uppercase tracking-widest">{isRtl ? 'قيود الاستخدام' : 'Usage Limits'}</h4>
                  {limitInput('messagesPerDay', 'Daily Messages Limit')}
                  {limitInput('agentsCount', 'Max Active Agents')}
                  {limitInput('flowsCount', 'Max Automation Flows')}
                  {limitInput('servicesPerDay', 'Daily AI Service Runs')}
                  
                  <div className="flex items-center gap-3 mt-4">
                      <label className="flex items-center gap-2 cursor-pointer bg-slate-100 dark:bg-white/5 px-4 py-3 rounded-xl w-full">
                          <input 
                              type="checkbox" 
                              checked={editingItem.limits?.allowProServices} 
                              onChange={(e) => setEditingItem({...editingItem, limits: {...editingItem.limits, allowProServices: e.target.checked}})} 
                              className="accent-emerald-500 w-4 h-4"
                          />
                          <span className="text-xs font-bold dark:text-white">Allow Pro Services (Dubbing, Legal, etc.)</span>
                      </label>
                  </div>
              </div>

              <div className="mb-4">
                 <label className="block text-xs font-bold text-slate-500 mb-1">Features (Comma Separated)</label>
                 <textarea 
                    value={editingItem.features?.join(', ') || ''} 
                    onChange={e => setEditingItem({...editingItem, features: e.target.value.split(',').map((s: string) => s.trim())})}
                    className="w-full bg-slate-50 dark:bg-black/20 border border-slate-200 dark:border-white/10 rounded-xl p-3 text-sm dark:text-white outline-none focus:border-emerald-500"
                 />
              </div>
            </>
          )}

          {editType === 'service' && (
            <>
              {commonInput('title', isRtl ? 'اسم الخدمة' : 'Service Title')}
              {commonTextArea('description', isRtl ? 'الوصف' : 'Description')}
              {commonInput('icon', 'FontAwesome Icon')}
              {commonInput('category', isRtl ? 'التصنيف' : 'Category')}
              <div className="flex items-center gap-4 mb-4">
                 <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editingItem.isPro} onChange={e => setEditingItem({...editingItem, isPro: e.target.checked})} className="accent-emerald-500"/>
                    <span className="text-sm font-bold dark:text-white">Pro Only</span>
                 </label>
              </div>
            </>
          )}

          {editType === 'integration' && (
            <>
              {commonInput('name', isRtl ? 'اسم التطبيق' : 'App Name')}
              {commonTextArea('description', isRtl ? 'الوصف' : 'Description')}
              {commonInput('icon', 'FontAwesome Icon')}
            </>
          )}

          <div className="flex gap-3 mt-6">
            <button onClick={() => setEditingItem(null)} className="flex-1 py-3 bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-white font-bold rounded-xl">{isRtl ? 'إلغاء' : 'Cancel'}</button>
            <button onClick={() => {
               if (editType === 'plan') handleSavePlan(editingItem);
               else if (editType === 'service') handleSaveService(editingItem);
               else handleSaveIntegration(editingItem);
            }} className="flex-1 py-3 bg-emerald-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/20">{isRtl ? 'حفظ' : 'Save'}</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 flex flex-col h-full bg-slate-50 dark:bg-[#050507] overflow-hidden font-sans relative">
      {renderForm()}
      
      {/* Header */}
      <div className="px-6 py-5 bg-white dark:bg-[#15151a] border-b border-slate-100 dark:border-white/5 flex items-center justify-between">
         <div className="flex items-center gap-4">
            <button onClick={onBack} className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-white/5 flex items-center justify-center text-slate-500 hover:text-emerald-500 transition-colors">
               <i className={`fas fa-arrow-${isRtl ? 'right' : 'left'}`}></i>
            </button>
            <h1 className="text-xl font-black text-slate-800 dark:text-white">{t.adminMode}</h1>
         </div>
      </div>

      {/* Tabs */}
      <div className="px-6 pt-6">
         <div className="flex bg-slate-200 dark:bg-white/5 p-1 rounded-xl w-full max-w-2xl overflow-x-auto">
            {(['overview', 'services', 'plans', 'permissions', 'integrations'] as const).map(tab => (
               <button 
                 key={tab}
                 onClick={() => setActiveTab(tab)}
                 className={`flex-1 py-2.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all whitespace-nowrap px-4 ${activeTab === tab ? 'bg-white dark:bg-emerald-500 text-slate-800 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
               >
                 {tab === 'overview' ? (isRtl ? 'نظرة عامة' : 'Overview') : tab === 'permissions' ? (isRtl ? 'الصلاحيات' : 'Permissions') : tab}
               </button>
            ))}
         </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 lg:p-10 custom-scrollbar">
         {activeTab === 'overview' && (
            <AdminDashboard 
              language={language} 
              services={services} 
              integrations={integrations} 
              currentMode={config.dataSourceMode || 'local'}
              onUpdateMode={handleUpdateMode}
            />
         )}

         {activeTab === 'permissions' && (
            <AdminPermissions language={language} plans={plans} services={services} />
         )}

         {/* ... (Plans, Services, Integrations tabs remain largely same, just rendering list) ... */}
         {activeTab === 'plans' && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
               <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-black dark:text-white">Plans Management</h3>
                  <button onClick={() => { setEditingItem({ id: Date.now().toString(), features: [], limits: { messagesPerDay: 10, agentsCount: 1, flowsCount: 0, servicesPerDay: 5, allowProServices: false } }); setEditType('plan'); }} className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-xs font-bold shadow-lg shadow-emerald-500/20"><i className="fas fa-plus"></i> Add Plan</button>
               </div>
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {plans.map(plan => (
                     <div key={plan.id} className={`bg-white dark:bg-[#1a1a20] p-6 rounded-2xl border border-slate-200 dark:border-white/5 relative group transition-opacity ${plan.hidden ? 'opacity-50 grayscale' : 'opacity-100'}`}>
                        {plan.hidden && <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"><span className="bg-black/50 text-white px-3 py-1 rounded-full text-xs font-bold uppercase backdrop-blur-sm">Hidden</span></div>}
                        <div className="flex justify-between items-start mb-4">
                           <div className={`w-12 h-12 rounded-xl bg-${plan.color || 'slate'}-500/10 flex items-center justify-center text-${plan.color || 'slate'}-500 text-xl`}><i className={`fas ${plan.icon}`}></i></div>
                           <div className="flex gap-2">
                              <button onClick={() => handleToggleVisibility(plan.id, 'plan')} className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 text-slate-500 flex items-center justify-center hover:bg-slate-200 dark:hover:bg-white/10 transition-all z-20" title={plan.hidden ? 'Show' : 'Hide'}>
                                 <i className={`fas ${plan.hidden ? 'fa-eye-slash' : 'fa-eye'} text-xs`}></i>
                              </button>
                              <button onClick={() => { setEditingItem(plan); setEditType('plan'); }} className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 text-blue-500 flex items-center justify-center hover:bg-blue-500 hover:text-white transition-all z-20"><i className="fas fa-pen text-xs"></i></button>
                              <button onClick={() => handleDeletePlan(plan.id)} className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-white/5 text-rose-500 flex items-center justify-center hover:bg-rose-500 hover:text-white transition-all z-20"><i className="fas fa-trash text-xs"></i></button>
                           </div>
                        </div>
                        <h4 className="text-base font-black dark:text-white">{plan.name}</h4>
                        <p className="text-2xl font-black text-emerald-500 my-2">{plan.price} <span className="text-xs text-slate-400 font-bold">/ {plan.period}</span></p>
                        
                        {/* Limits Preview */}
                        <div className="bg-slate-50 dark:bg-white/5 p-2 rounded-lg text-[9px] font-mono text-slate-500 mb-2 grid grid-cols-2 gap-1">
                            <div>Msgs: {plan.limits?.messagesPerDay === -1 ? '∞' : plan.limits?.messagesPerDay}</div>
                            <div>Agents: {plan.limits?.agentsCount}</div>
                            <div>Flows: {plan.limits?.flowsCount}</div>
                            <div>Svcs: {plan.limits?.servicesPerDay === -1 ? '∞' : plan.limits?.servicesPerDay}</div>
                        </div>

                        <div className="space-y-1">
                           {plan.features.slice(0,3).map((f,i) => <p key={i} className="text-[10px] text-slate-500 dark:text-slate-400">• {f}</p>)}
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         )}

         {activeTab === 'services' && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
               <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-black dark:text-white">Services Management</h3>
                  <button onClick={() => { setEditingItem({ id: Date.now().toString(), category: 'media', color: 'from-blue-500 to-indigo-600' }); setEditType('service'); }} className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-xs font-bold shadow-lg shadow-emerald-500/20"><i className="fas fa-plus"></i> Add Service</button>
               </div>
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {services.map(service => (
                     <div key={service.id} className={`bg-white dark:bg-[#1a1a20] p-5 rounded-2xl border border-slate-200 dark:border-white/5 relative group hover:border-emerald-500/30 transition-all ${service.hidden ? 'opacity-50 grayscale' : 'opacity-100'}`}>
                        {service.hidden && <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"><span className="bg-black/50 text-white px-3 py-1 rounded-full text-xs font-bold uppercase backdrop-blur-sm">Hidden</span></div>}
                        <div className="flex justify-between items-start mb-3">
                           <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${service.color} flex items-center justify-center text-white`}><i className={`fas ${service.icon}`}></i></div>
                           <div className="flex gap-2 opacity-50 group-hover:opacity-100 transition-opacity z-20">
                              <button onClick={() => handleToggleVisibility(service.id, 'service')} className="text-slate-500 hover:text-slate-300" title={service.hidden ? 'Show' : 'Hide'}><i className={`fas ${service.hidden ? 'fa-eye-slash' : 'fa-eye'}`}></i></button>
                              <button onClick={() => { setEditingItem(service); setEditType('service'); }} className="text-blue-500 hover:text-blue-400"><i className="fas fa-pen"></i></button>
                              <button onClick={() => handleDeleteService(service.id)} className="text-rose-500 hover:text-rose-400"><i className="fas fa-trash"></i></button>
                           </div>
                        </div>
                        <h4 className="text-sm font-black dark:text-white mb-1">{service.title}</h4>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-2">{service.description}</p>
                     </div>
                  ))}
               </div>
            </div>
         )}

         {activeTab === 'integrations' && (
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2">
               <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-black dark:text-white">Integrations Management</h3>
                  <button onClick={() => { setEditingItem({ id: Date.now().toString(), color: 'from-slate-500 to-slate-700', connected: false }); setEditType('integration'); }} className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-xs font-bold shadow-lg shadow-emerald-500/20"><i className="fas fa-plus"></i> Add Integration</button>
               </div>
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {integrations.map(app => (
                     <div key={app.id} className={`bg-white dark:bg-[#1a1a20] p-5 rounded-2xl border border-slate-200 dark:border-white/5 flex items-center gap-4 group transition-all ${app.hidden ? 'opacity-50 grayscale' : 'opacity-100'}`}>
                        {app.hidden && <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"><span className="bg-black/50 text-white px-3 py-1 rounded-full text-xs font-bold uppercase backdrop-blur-sm">Hidden</span></div>}
                        <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${app.color} flex items-center justify-center text-white text-xl`}><i className={`fa-brands ${app.icon.replace('fa-', '')} ${app.icon}`}></i></div>
                        <div className="flex-1 min-w-0">
                           <h4 className="text-sm font-black dark:text-white truncate">{app.name}</h4>
                           <p className="text-[10px] text-slate-500 truncate">{app.description}</p>
                        </div>
                        <div className="flex flex-col gap-2 opacity-50 group-hover:opacity-100 transition-opacity z-20">
                           <button onClick={() => handleToggleVisibility(app.id, 'integration')} className="text-slate-500 hover:text-slate-300" title={app.hidden ? 'Show' : 'Hide'}><i className={`fas ${app.hidden ? 'fa-eye-slash' : 'fa-eye'} text-xs`}></i></button>
                           <button onClick={() => { setEditingItem(app); setEditType('integration'); }} className="text-blue-500 hover:text-blue-400"><i className="fas fa-pen text-xs"></i></button>
                           <button onClick={() => handleDeleteIntegration(app.id)} className="text-rose-500 hover:text-rose-400"><i className="fas fa-trash text-xs"></i></button>
                        </div>
                     </div>
                  ))}
               </div>
            </div>
         )}
      </div>
    </div>
  );
};

export default AdminTemplate;